import { Box, Flex, HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { useThemeContext } from '$src/context/ThemeContext';
import { useLockBackgroundScroll } from '$src/hooks/client/useLockBackgroundScroll';
import { getThemeColors } from '$src/utils/themeColors';

// iOS spring-ish ease, matching the hero sheets.
const IOS_CURVE = [0.32, 0.72, 0, 1] as const;

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MinusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export interface QuantitySheetProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl?: string;
  productName: string;
  /** Current committed quantity; seeds the draft each time the sheet opens. */
  quantity: number;
  /** Upper bound from stock, when known. */
  maxQuantity?: number;
  isPending?: boolean;
  /** Commit the chosen quantity. The parent closes the sheet on success. */
  onUpdate: (newQuantity: number) => void;
  /** When provided, a "Remove" action is shown (cart line items). */
  onRemove?: () => void;
}

/**
 * Shared quantity-edit bottom sheet — the mobile hero flow, reused for cart line
 * items. Scrim + slide-up sheet + −/＋ stepper + "Update", with an optional
 * "Remove" action. Rendered inline (not portaled) so framer-motion animates it.
 */
export const QuantitySheet = ({
  isOpen,
  onClose,
  imageUrl,
  productName,
  quantity,
  maxQuantity,
  isPending = false,
  onUpdate,
  onRemove,
}: QuantitySheetProps) => {
  const { t } = useTranslation('common');
  const { themeName } = useThemeContext();
  const colors = getThemeColors(themeName);
  useLockBackgroundScroll(isOpen);

  const [draft, setDraft] = useState(Math.max(1, quantity));
  useEffect(() => {
    if (isOpen) setDraft(Math.max(1, quantity));
  }, [isOpen, quantity]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="qty-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: IOS_CURVE }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.4)', zIndex: 2000 }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="qty-sheet"
            role="dialog"
            aria-modal="true"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.35, ease: IOS_CURVE }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2001,
              background: colors.cardBg,
              borderTopLeftRadius: '1.25rem',
              borderTopRightRadius: '1.25rem',
              boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.18)',
            }}
          >
            <Box px={4} pt={4} pb="calc(1rem + env(safe-area-inset-bottom))">
              {/* Header: thumbnail + "Update quantity" + product name + close */}
              <HStack spacing={3} align="center" mb={6}>
                <Box
                  flexShrink={0}
                  w="2.75rem"
                  h="2.75rem"
                  borderRadius="0.5rem"
                  overflow="hidden"
                  bg={colors.bgSecondary}
                >
                  {imageUrl ? (
                    <Image src={imageUrl} alt={productName} w="full" h="full" objectFit="cover" />
                  ) : null}
                </Box>
                <VStack flex="1" align="start" spacing={0} minW={0}>
                  <Text color={colors.text} fontWeight={600} fontSize="1rem" noOfLines={1}>
                    {t('commons.update_quantity')}
                  </Text>
                  <Text color={colors.textMuted} fontSize="0.875rem" noOfLines={1}>
                    {productName}
                  </Text>
                </VStack>
                <Box
                  as="button"
                  type="button"
                  onClick={onClose}
                  flexShrink={0}
                  color={colors.textMuted}
                  p={1}
                  aria-label={t('buttons.close')}
                >
                  <CloseIcon />
                </Box>
              </HStack>

              {/* Stepper: − [value] + */}
              <HStack justify="center" spacing={5} mb={6}>
                <Flex
                  as="button"
                  type="button"
                  onClick={() => setDraft((q) => Math.max(1, q - 1))}
                  disabled={draft <= 1}
                  w="2.75rem"
                  h="2.75rem"
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg={colors.bgSecondary}
                  color={colors.text}
                  opacity={draft <= 1 ? 0.4 : 1}
                  transition="opacity 150ms ease"
                  aria-label={t('commons.update_quantity')}
                >
                  <MinusIcon />
                </Flex>
                <Flex
                  align="center"
                  justify="center"
                  minW="4rem"
                  h="2.75rem"
                  px={3}
                  borderRadius="0.625rem"
                  border={`1px solid ${colors.border}`}
                  color={colors.text}
                  fontWeight={600}
                  fontSize="1.125rem"
                >
                  {draft}
                </Flex>
                <Flex
                  as="button"
                  type="button"
                  onClick={() => setDraft((q) => (maxQuantity ? Math.min(maxQuantity, q + 1) : q + 1))}
                  disabled={maxQuantity ? draft >= maxQuantity : false}
                  w="2.75rem"
                  h="2.75rem"
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg={colors.bgSecondary}
                  color={colors.text}
                  opacity={maxQuantity && draft >= maxQuantity ? 0.4 : 1}
                  transition="opacity 150ms ease"
                  aria-label={t('commons.update_quantity')}
                >
                  <PlusIcon />
                </Flex>
              </HStack>

              {/* Commit */}
              <Flex
                as="button"
                type="button"
                onClick={() => onUpdate(draft)}
                disabled={isPending}
                w="full"
                h="3.25rem"
                align="center"
                justify="center"
                borderRadius="0.75rem"
                bg="var(--pay-button-bg-enabled, #1C1C1C)"
                color="var(--pay-button-text-enabled, #FFFFFF)"
                fontWeight={600}
                fontSize="1rem"
                opacity={isPending ? 0.7 : 1}
                transition="opacity 150ms ease"
              >
                {isPending ? <Spinner size="sm" /> : t('buttons.update')}
              </Flex>

              {/* Optional remove (cart line items) */}
              {onRemove && (
                <Flex
                  as="button"
                  type="button"
                  onClick={onRemove}
                  disabled={isPending}
                  w="full"
                  mt={3}
                  h="2.75rem"
                  align="center"
                  justify="center"
                  color="var(--danger, #EE5829)"
                  fontWeight={500}
                  fontSize="0.9375rem"
                  data-ph-capture-attribute-tracking-id="quantity-sheet-remove"
                >
                  {t('commons.delete')}
                </Flex>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
